import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import {
  Mic,
  MicOff,
  X,
  Volume2,
  VolumeX,
  Loader2,
  Sparkles,
  Phone,
  PhoneOff
} from 'lucide-react';

import { API_URL } from '../config';

// Wake word detection patterns
const WAKE_WORDS = ['bonjour julia', 'hello julia', 'hey julia', 'ok julia'];

export default function JuliaVoiceAssistant() {
  const [isOpen, setIsOpen] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [juliaResponse, setJuliaResponse] = useState('');
  const [wakeWordDetected, setWakeWordDetected] = useState(false);
  
  const peerConnectionRef = useRef(null);
  const dataChannelRef = useRef(null);
  const audioElementRef = useRef(null);
  const localStreamRef = useRef(null);
  const wakeWordRecognitionRef = useRef(null);

  // Initialize wake word detection using Web Speech API
  useEffect(() => {
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      const recognition = new SpeechRecognition();
      recognition.continuous = true;
      recognition.interimResults = true;
      recognition.lang = 'fr-FR';

      recognition.onresult = (event) => {
        const last = event.results.length - 1;
        const text = event.results[last][0].transcript.toLowerCase().trim();
        
        // Check for wake word
        for (const wakeWord of WAKE_WORDS) {
          if (text.includes(wakeWord)) {
            setWakeWordDetected(true);
            recognition.stop();
            // Auto-connect when wake word is detected
            if (!isConnected && !isConnecting) {
              handleConnect();
            }
            toast.success('Julia activée ! 🎤', {
              description: 'Je vous écoute...'
            });
            break;
          }
        }
      };

      recognition.onerror = (event) => {
        if (event.error !== 'no-speech') {
          console.error('Wake word recognition error:', event.error);
        }
      };

      recognition.onend = () => {
        // Restart wake word detection if not connected
        if (!isConnected && !isConnecting) {
          try {
            recognition.start();
          } catch (e) {
            // Ignore if already started
          }
        }
      };

      wakeWordRecognitionRef.current = recognition;

      // Start wake word detection
      try {
        recognition.start();
      } catch (e) {
        console.error('Could not start wake word detection:', e);
      }
    }

    return () => {
      if (wakeWordRecognitionRef.current) {
        wakeWordRecognitionRef.current.stop();
      }
    };
  }, [isConnected, isConnecting]);

  const handleConnect = async () => {
    if (isConnecting || isConnected) return;
    
    // Check if microphone is available
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      toast.error('Navigateur non compatible', {
        description: 'Votre navigateur ne supporte pas l\'accès au microphone'
      });
      return;
    }
    
    setIsConnecting(true);
    
    try {
      // Stop wake word detection
      if (wakeWordRecognitionRef.current) {
        wakeWordRecognitionRef.current.stop();
      }

      // Get session token from backend
      const tokenResponse = await fetch(`${API_URL}/api/julia/realtime/session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      const data = await tokenResponse.json();
      if (!data.client_secret?.value) {
        throw new Error('Failed to get session token');
      }

      // Create WebRTC peer connection
      const pc = new RTCPeerConnection({
        iceServers: [{ urls: 'stun:stun.l.google.com:19302' }]
      });
      peerConnectionRef.current = pc;

      // Set up audio element for Julia's voice
      const audioEl = document.createElement('audio');
      audioEl.autoplay = true;
      audioElementRef.current = audioEl;

      pc.ontrack = (event) => {
        audioEl.srcObject = event.streams[0];
        setIsSpeaking(true);
      };

      // Get local audio stream (user's microphone)
      let stream;
      try {
        stream = await navigator.mediaDevices.getUserMedia({ 
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true
          } 
        });
        localStreamRef.current = stream;
      } catch (micError) {
        console.error('Microphone access error:', micError);
        let errorMessage = 'Impossible d\'accéder au microphone';
        
        if (micError.name === 'NotFoundError') {
          errorMessage = 'Aucun microphone détecté. Veuillez vérifier votre matériel.';
        } else if (micError.name === 'NotAllowedError' || micError.name === 'PermissionDeniedError') {
          errorMessage = 'Autorisation microphone refusée. Activez-la dans les paramètres du navigateur.';
        } else if (micError.name === 'NotReadableError') {
          errorMessage = 'Microphone déjà utilisé par une autre application.';
        }
        
        throw new Error(errorMessage);
      }
      
      stream.getTracks().forEach(track => {
        pc.addTrack(track, stream);
      });
      setIsListening(true);

      // Set up data channel for events
      const dc = pc.createDataChannel('oai-events');
      dataChannelRef.current = dc;

      dc.onmessage = (event) => {
        try {
          const eventData = JSON.parse(event.data);
          handleRealtimeEvent(eventData);
        } catch (e) {
          console.log('Received event:', event.data);
        }
      };

      dc.onopen = () => {
        console.log('Data channel opened');
        setIsConnected(true);
        setIsConnecting(false);
        
        // Send Julia's system instructions with full UrgenceMed context
        const systemInstructions = {
          type: 'session.update',
          session: {
            instructions: `Tu es Julia, une assistante médicale IA vocale pour UrgenceMed.

PERSONNALITÉ:
- Tu es professionnelle, calme et rassurante
- Tu parles en français par défaut, mais tu réponds en anglais si on te parle en anglais
- Tu es concise et précise dans tes réponses vocales (maximum 30 secondes)
- Tu utilises un langage médical approprié mais accessible

BASE DE DONNÉES URGENCEMED - Tu as accès à:

1. MÉDICAMENTS D'URGENCE (182 médicaments):
   - 116 médicaments pour ADULTES
   - 66 médicaments PÉDIATRIQUES
   Exemples: Adrénaline (0.01 mg/kg ACR, max 1mg), Amiodarone (300mg bolus ACR), Midazolam, Morphine, etc.

2. PROTOCOLES D'URGENCE:
   - AVC Expert: Critères thrombolyse, thrombectomie, contrôle TA
   - SCA ST+: Algorithme coronaro vs fibrinolyse, traitements adjuvants
   - Trauma Sévère: Red Flags, bilan complet, scores
   - Intubation: Séquence rapide adulte et pédiatrique avec doses
   - MARCHE Trauma: Évaluation systématique pré-hospitalière
   - Arrêt Cardiaque: Protocoles ACR adulte et pédiatrique

3. OUTILS SPÉCIALISÉS:
   - Gaz du Sang: Interprétation pH, PaCO2, HCO3, trou anionique
   - LCR Méningites: Diagnostic différentiel et antibiothérapie HAS/SPILF
   - Antibiotiques: 72 guides par pathologie (pneumonie, méningite, sepsis, etc.)
   - Calculateurs: Doses pédiatriques auto, débits perfusion, clairance créatinine

4. SCORES MÉDICAUX (46 scores):
   - Glasgow (coma), NIHSS (AVC), Wells (EP/TVP)
   - CHA2DS2-VASc (FA), GRACE (SCA), SOFA (défaillance)
   - Score Ramsay (sédation), Red Flags (trauma)

5. TECHNIQUES:
   - Intubation: Matériel, drogues (Kétamine 1-2mg/kg, Célocurine 1mg/kg)
   - Voie Intraosseuse: Sites (tibia proximal, humérus), EZ-IO
   - Drainage thoracique, Ponction lombaire, Ponction péricardique
   - Accouchement inopiné, Sédation procédurale

6. CARDIOVASCULAIRE:
   - SCA ST+: Territoires ECG, critères Sgarbossa
   - NSTEMI: Score GRACE, stratification
   - EES: Critères, étiologies
   - Tamponnade: Diagnostic et drainage

7. VENTILATION:
   - VNI: Indications, réglages initiaux
   - Ventilation mécanique: Modes, paramètres
   - Modes ventilatoires détaillés

RÈGLES IMPORTANTES:
- Toujours rappeler que tu ne remplaces pas un médecin
- En cas d'urgence vitale, conseiller d'appeler le 15 (SAMU) ou le 112
- Demander des précisions si nécessaire (âge, poids, allergies, contexte)
- Être prudente avec les posologies pédiatriques
- Citer les sources (protocoles HAS, SFMU, ESC, AHA)
- Reste concise: réponds en 20-30 secondes maximum

EXEMPLE DE RÉPONSE COURTE:
Question: "Posologie adrénaline ACR adulte?"
Réponse: "Pour un arrêt cardiaque adulte, adrénaline 1 milligramme en intra-veineuse ou intra-osseuse, à renouveler toutes les 3 à 5 minutes. C'est 10 millilitres d'adrénaline au 1/10000ème."

Commence par te présenter brièvement: "Bonjour, je suis Julia, votre assistante médicale UrgenceMed. Comment puis-je vous aider?"`,
            voice: 'sage',
            modalities: ['audio', 'text'],
            turn_detection: {
              type: 'server_vad',
              threshold: 0.5,
              prefix_padding_ms: 300,
              silence_duration_ms: 500
            }
          }
        };
        
        try {
          dc.send(JSON.stringify(systemInstructions));
          console.log('Sent Julia system instructions');
        } catch (e) {
          console.error('Failed to send system instructions:', e);
        }
        
        toast.success('Julia connectée !', {
          description: 'Parlez-moi de votre question médicale'
        });
      };

      dc.onclose = () => {
        console.log('Data channel closed');
        handleDisconnect();
      };

      // Create and send offer
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);

      // Send offer to backend and get answer
      const response = await fetch(`${API_URL}/api/julia/realtime/negotiate`, {
        method: 'POST',
        body: offer.sdp,
        headers: {
          'Content-Type': 'application/sdp'
        }
      });

      const { sdp: answerSdp } = await response.json();
      const answer = {
        type: 'answer',
        sdp: answerSdp
      };

      await pc.setRemoteDescription(answer);
      console.log('WebRTC connection established');

    } catch (error) {
      console.error('Failed to connect:', error);
      
      let errorTitle = 'Erreur de connexion';
      let errorDescription = error.message || 'Impossible de connecter Julia';
      
      // Messages d'erreur spécifiques selon le type
      if (error.message.includes('microphone')) {
        errorTitle = 'Problème de microphone';
      } else if (error.message.includes('session token')) {
        errorTitle = 'Erreur d\'authentification';
        errorDescription = 'Impossible d\'obtenir le token de session';
      } else if (error.message.includes('WebRTC')) {
        errorTitle = 'Erreur de connexion audio';
        errorDescription = 'La connexion audio en temps réel a échoué';
      }
      
      toast.error(errorTitle, {
        description: errorDescription,
        duration: 5000
      });
      setIsConnecting(false);
      handleDisconnect();
    }
  };

  const handleRealtimeEvent = (eventData) => {
    switch (eventData.type) {
      case 'response.audio_transcript.delta':
        setJuliaResponse(prev => prev + (eventData.delta || ''));
        break;
      case 'response.audio_transcript.done':
        // Julia finished speaking
        setIsSpeaking(false);
        break;
      case 'input_audio_buffer.speech_started':
        setIsListening(true);
        setTranscript('');
        break;
      case 'input_audio_buffer.speech_stopped':
        setIsListening(false);
        break;
      case 'conversation.item.input_audio_transcription.completed':
        setTranscript(eventData.transcript || '');
        break;
      case 'response.done':
        setIsSpeaking(false);
        break;
      default:
        // Handle other events
        break;
    }
  };

  const handleDisconnect = useCallback(() => {
    // Close peer connection
    if (peerConnectionRef.current) {
      peerConnectionRef.current.close();
      peerConnectionRef.current = null;
    }

    // Stop local audio stream
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach(track => track.stop());
      localStreamRef.current = null;
    }

    // Close data channel
    if (dataChannelRef.current) {
      dataChannelRef.current.close();
      dataChannelRef.current = null;
    }

    // Remove audio element
    if (audioElementRef.current) {
      audioElementRef.current.srcObject = null;
      audioElementRef.current = null;
    }

    setIsConnected(false);
    setIsConnecting(false);
    setIsListening(false);
    setIsSpeaking(false);
    setWakeWordDetected(false);
    setTranscript('');
    setJuliaResponse('');

    // Restart wake word detection
    if (wakeWordRecognitionRef.current) {
      try {
        wakeWordRecognitionRef.current.start();
      } catch (e) {
        // Ignore
      }
    }

    toast.info('Julia déconnectée', {
      description: 'Dites "Bonjour Julia" pour me réactiver'
    });
  }, []);

  const toggleMute = () => {
    if (localStreamRef.current) {
      const audioTrack = localStreamRef.current.getAudioTracks()[0];
      if (audioTrack) {
        audioTrack.enabled = !audioTrack.enabled;
        setIsListening(audioTrack.enabled);
      }
    }
  };

  return (
    <>
      {/* Floating Button */}
      <div className="fixed bottom-20 md:bottom-6 right-4 z-50">
        <Button
          onClick={() => setIsOpen(!isOpen)}
          className={`
            w-14 h-14 rounded-full shadow-lg transition-all duration-300 overflow-hidden p-0 border-2
            ${isConnected 
              ? 'border-teal-400 hover:border-teal-300' 
              : 'border-purple-400 hover:border-purple-300'
            }
            ${isSpeaking ? 'animate-pulse ring-4 ring-purple-400/50' : ''}
          `}
          data-testid="julia-floating-button"
        >
          {isConnecting ? (
            <div className="w-full h-full bg-gradient-to-r from-purple-500 to-pink-500 flex items-center justify-center">
              <Loader2 className="w-6 h-6 text-white animate-spin" />
            </div>
          ) : (
            <img 
              src="https://customer-assets.emergentagent.com/job_med-emergency-ai/artifacts/fazbrqes_IMG_6197.jpeg"
              alt="Julia AI Assistant"
              className="w-full h-full object-cover"
            />
          )}
        </Button>
        
        {/* Connection indicator */}
        {isConnected && (
          <span className="absolute top-0 right-0 w-4 h-4 bg-green-500 rounded-full border-2 border-white animate-pulse shadow-lg" />
        )}
        
        {/* Speaking indicator */}
        {isSpeaking && (
          <span className="absolute bottom-0 left-0 w-4 h-4 bg-purple-500 rounded-full border-2 border-white animate-pulse shadow-lg" />
        )}
      </div>

      {/* Voice Assistant Panel */}
      {isOpen && (
        <div 
          className="fixed bottom-36 md:bottom-24 right-4 w-80 md:w-96 z-50 animate-scale-in"
          data-testid="julia-panel"
        >
          <div className="glass-card rounded-2xl border border-[hsl(var(--border))] shadow-2xl overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-r from-purple-500/20 to-pink-500/20 p-4 border-b border-[hsl(var(--border))]">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`
                    w-10 h-10 rounded-full flex items-center justify-center
                    ${isConnected 
                      ? 'bg-gradient-to-r from-teal-500 to-cyan-500' 
                      : 'bg-gradient-to-r from-purple-500 to-pink-500'
                    }
                  `}>
                    <Sparkles className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-white">Julia</h3>
                    <p className="text-xs text-[hsl(var(--text-dim))]">
                      {isConnected ? 'En ligne' : isConnecting ? 'Connexion...' : 'Hors ligne'}
                    </p>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setIsOpen(false)}
                  className="text-[hsl(var(--text-dim))] hover:text-white"
                >
                  <X className="w-5 h-5" />
                </Button>
              </div>
            </div>

            {/* Content */}
            <div className="p-4 space-y-4 max-h-80 overflow-y-auto">
              {!isConnected ? (
                <div className="text-center py-6">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-r from-purple-500/20 to-pink-500/20 flex items-center justify-center">
                    <Mic className="w-8 h-8 text-purple-400" />
                  </div>
                  <p className="text-sm text-[hsl(var(--text-dim))] mb-4">
                    Dites <span className="text-purple-400 font-medium">"Bonjour Julia"</span> pour m'activer
                    <br />ou cliquez sur le bouton ci-dessous
                  </p>
                  <Button
                    onClick={handleConnect}
                    disabled={isConnecting}
                    className="bg-gradient-to-r from-purple-500 to-pink-500 text-white"
                    data-testid="julia-connect-button"
                  >
                    {isConnecting ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Connexion...
                      </>
                    ) : (
                      <>
                        <Phone className="w-4 h-4 mr-2" />
                        Activer Julia
                      </>
                    )}
                  </Button>
                </div>
              ) : (
                <>
                  {/* Status indicators */}
                  <div className="flex items-center gap-2 text-sm">
                    <div className={`flex items-center gap-1 px-2 py-1 rounded-full ${isListening ? 'bg-green-500/20 text-green-400' : 'bg-gray-500/20 text-gray-400'}`}>
                      {isListening ? <Mic className="w-3 h-3" /> : <MicOff className="w-3 h-3" />}
                      <span>{isListening ? 'Écoute' : 'Muet'}</span>
                    </div>
                    <div className={`flex items-center gap-1 px-2 py-1 rounded-full ${isSpeaking ? 'bg-purple-500/20 text-purple-400' : 'bg-gray-500/20 text-gray-400'}`}>
                      {isSpeaking ? <Volume2 className="w-3 h-3" /> : <VolumeX className="w-3 h-3" />}
                      <span>{isSpeaking ? 'Parle' : 'Silence'}</span>
                    </div>
                  </div>

                  {/* Transcript (what user said) */}
                  {transcript && (
                    <div className="p-3 rounded-lg bg-blue-500/10 border border-blue-500/30">
                      <p className="text-xs text-blue-400 mb-1">Vous :</p>
                      <p className="text-sm text-white">{transcript}</p>
                    </div>
                  )}

                  {/* Julia's response */}
                  {juliaResponse && (
                    <div className="p-3 rounded-lg bg-purple-500/10 border border-purple-500/30">
                      <p className="text-xs text-purple-400 mb-1">Julia :</p>
                      <p className="text-sm text-white">{juliaResponse}</p>
                    </div>
                  )}

                  {/* Listening animation */}
                  {isListening && !transcript && !juliaResponse && (
                    <div className="flex items-center justify-center py-6">
                      <div className="flex items-center gap-1">
                        {[...Array(5)].map((_, i) => (
                          <div
                            key={i}
                            className="w-1 bg-gradient-to-t from-purple-500 to-pink-500 rounded-full animate-pulse"
                            style={{
                              height: `${Math.random() * 20 + 10}px`,
                              animationDelay: `${i * 0.1}s`
                            }}
                          />
                        ))}
                      </div>
                      <span className="ml-3 text-sm text-[hsl(var(--text-dim))]">Je vous écoute...</span>
                    </div>
                  )}

                  {/* Speaking animation */}
                  {isSpeaking && (
                    <div className="flex items-center justify-center py-2">
                      <div className="flex items-center gap-1">
                        {[...Array(7)].map((_, i) => (
                          <div
                            key={i}
                            className="w-1 bg-gradient-to-t from-teal-500 to-cyan-500 rounded-full"
                            style={{
                              height: `${Math.random() * 24 + 8}px`,
                              animation: 'pulse 0.5s ease-in-out infinite',
                              animationDelay: `${i * 0.07}s`
                            }}
                          />
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Footer controls */}
            {isConnected && (
              <div className="p-4 border-t border-[hsl(var(--border))] flex items-center justify-between">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={toggleMute}
                  className={isListening ? 'text-green-400' : 'text-red-400'}
                >
                  {isListening ? <Mic className="w-4 h-4 mr-1" /> : <MicOff className="w-4 h-4 mr-1" />}
                  {isListening ? 'Muet' : 'Activer micro'}
                </Button>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={handleDisconnect}
                  data-testid="julia-disconnect-button"
                >
                  <PhoneOff className="w-4 h-4 mr-1" />
                  Raccrocher
                </Button>
              </div>
            )}

            {/* Help text */}
            <div className="px-4 pb-3">
              <p className="text-xs text-center text-[hsl(var(--muted))]">
                {isConnected 
                  ? 'Posez vos questions sur les médicaments, protocoles, scores médicaux...'
                  : 'Assistant médical IA vocal - Français & English'
                }
              </p>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
